<template>
  <div class="registration" :class="{ embedded: isEmbedded }">
    <div class="registration-container">
      <div class="left-panel">
        <div class="header">
          <p class="eyebrow">Biometrics</p>
          <h2><i class="mdi mdi-fingerprint"></i> Face enrollment</h2>
          <p class="subtitle">Confirm the student, capture three clear photos, then register.</p>
        </div>

        <div class="steps" aria-label="Enrollment steps">
          <div class="step" :class="{ active: activeStep === 1, done: activeStep > 1 }">
            <span class="step-num">1</span>
            <span class="step-label">Confirm</span>
          </div>
          <div class="step-line" :class="{ done: activeStep > 1 }"></div>
          <div class="step" :class="{ active: activeStep === 2, done: activeStep > 2 }">
            <span class="step-num">2</span>
            <span class="step-label">Capture</span>
          </div>
          <div class="step-line" :class="{ done: activeStep > 2 }"></div>
          <div class="step" :class="{ active: activeStep === 3, done: canSubmit }">
            <span class="step-num">3</span>
            <span class="step-label">Register</span>
          </div>
        </div>

        <div v-if="prefilledFromQuery && isStudentConfirmed" class="student-card">
          <div class="student-avatar" aria-hidden="true">
            {{ studentInitials }}
          </div>
          <div class="student-card-body">
            <p class="student-card-eyebrow">Confirmed student</p>
            <p class="student-card-name">{{ fullName }}</p>
            <p class="student-card-meta">{{ email || 'No email' }}</p>
          </div>
          <i class="mdi mdi-check-circle student-card-check" aria-hidden="true"></i>
        </div>

        <div v-else class="search-container">
          <p class="search-hint">Search an existing college-portal student, then capture 3 face photos.</p>
          <div class="search-wrapper">
            <i class="mdi mdi-magnify search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search student by name..."
              class="search-input"
              @input="handleSearch"
            />
          </div>

          <div v-if="searchResults.length > 0" class="search-results">
            <div
              v-for="student in searchResults"
              :key="student.id"
              class="search-result-item"
              @click="selectStudent(student)"
            >
              <div class="student-info">
                <div class="student-name">{{ student.name || 'No name' }}</div>
                <div class="student-email">{{ student.email || 'No email' }}</div>
              </div>
            </div>
          </div>

          <div v-if="isStudentConfirmed" class="form-fields">
            <div class="form-group">
              <i class="mdi mdi-id-card"></i>
              <input type="text" v-model="studentId" placeholder="Student ID" readonly>
            </div>
            <div class="form-group">
              <i class="mdi mdi-account"></i>
              <input type="text" v-model="fullName" placeholder="Full Name" readonly>
            </div>
            <div class="form-group">
              <i class="mdi mdi-email"></i>
              <input type="email" v-model="email" placeholder="Email" readonly>
            </div>
          </div>
        </div>

        <div class="photo-progress">
          <div class="photo-progress-header">
            <h3>Face photos</h3>
            <span class="photo-count">{{ capturedImages.length }}/3</span>
          </div>
          <p class="photo-guidance">{{ photoGuidance }}</p>
          <div class="photo-slots">
            <div
              v-for="slot in 3"
              :key="slot"
              class="photo-slot"
              :class="{ filled: capturedImages[slot - 1] }"
            >
              <img
                v-if="capturedImages[slot - 1]"
                :src="capturedImages[slot - 1]"
                :alt="`Captured face ${slot}`"
              />
              <template v-else>
                <i class="mdi mdi-account-outline"></i>
                <span>{{ slot }}</span>
              </template>
              <button
                v-if="capturedImages[slot - 1]"
                type="button"
                class="remove-btn"
                :aria-label="`Remove photo ${slot}`"
                @click="removeImage(slot - 1)"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="dialog.show" class="dialog-overlay" @click="closeDialog">
          <div class="dialog" @click.stop>
            <div class="dialog-content">
              <i :class="['mdi', dialog.isSuccess ? 'mdi-check-circle success-icon' : 'mdi-alert error-icon']"></i>
              <h3>{{ dialog.message }}</h3>
              <button type="button" @click="closeDialog" class="dialog-btn">
                OK
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="submit-btn"
          :disabled="!canSubmit || isRegistering"
          @click="submitRegistration"
        >
          <template v-if="!isRegistering">
            <i class="mdi mdi-check-circle"></i>
            Register biometrics
          </template>
          <template v-else>
            <i class="mdi mdi-loading mdi-spin"></i>
            Registering…
          </template>
        </button>
      </div>

      <div class="right-panel">
        <div class="camera-section">
          <div class="camera-container">
            <div class="video-wrapper">
              <div class="camera-frame">
                <div class="frame-header">{{ photoGuidance.toUpperCase() }}</div>
                <div class="frame-corners">
                  <div class="corner top-left"></div>
                  <div class="corner top-right"></div>
                  <div class="corner bottom-left"></div>
                  <div class="corner bottom-right"></div>
                </div>
                <div class="frame-guide"></div>
              </div>

              <video ref="video" autoplay playsinline></video>
              <canvas ref="canvas" style="display:none"></canvas>

              <div class="camera-overlay" v-if="!isCameraStarted">
                <div class="camera-overlay-card">
                  <i class="mdi mdi-camera-outline overlay-icon"></i>
                  <p class="overlay-title">Ready to capture</p>
                  <p class="overlay-text">Allow camera access, then align the face in the guide.</p>
                  <button type="button" @click="startCamera" class="camera-btn">
                    <i class="mdi mdi-camera"></i> Start camera
                  </button>
                </div>
              </div>

              <div class="camera-controls" v-if="isCameraStarted">
                <button
                  type="button"
                  class="capture-btn"
                  :disabled="capturedImages.length >= 3 || isProcessing || !isStudentConfirmed"
                  :title="!isStudentConfirmed ? 'Confirm a student first' : 'Capture photo'"
                  @click="captureImage"
                >
                  <i class="mdi mdi-camera" v-if="!isProcessing"></i>
                  <i class="mdi mdi-loading mdi-spin" v-else></i>
                </button>
                <p class="capture-hint">{{ captureHint }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { edClockApiUrl } from '@/utils/edClockApi';

export default {
  name: 'RegistrationEdstackView',
  data() {
    return {
      studentId: '',
      edClockStudentId: '',
      fullName: '',
      email: '',
      stream: null,
      capturedImages: [],
      isCameraStarted: false,
      error: null,
      searchQuery: '',
      searchResults: [],
      showResults: false,
      debounceTimer: null,
      isProcessing: false,
      isRegistering: false,
      prefilledFromQuery: false,
      dialog: {
        show: false,
        message: '',
        isSuccess: true
      }
    }
  },
  computed: {
    canSubmit() {
      return this.studentId &&
             this.fullName &&
             this.capturedImages.length >= 3;
    },
    isEmbedded() {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    },
    isStudentConfirmed() {
      return Boolean(this.studentId && this.fullName);
    },
    studentInitials() {
      const parts = String(this.fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
      }
      return (parts[0]?.[0] || '?').toUpperCase();
    },
    photoGuidance() {
      if (!this.isStudentConfirmed) {
        return 'Confirm the student first';
      }
      if (this.capturedImages.length >= 3) {
        return 'All 3 photos captured';
      }
      return `Photo ${this.capturedImages.length + 1} of 3`;
    },
    captureHint() {
      if (!this.isStudentConfirmed) {
        return 'Confirm a student to capture';
      }
      if (this.capturedImages.length >= 3) {
        return 'Ready to register';
      }
      return 'Tap to capture';
    },
    activeStep() {
      if (!this.isStudentConfirmed) {
        return 1;
      }
      if (this.capturedImages.length < 3) {
        return 2;
      }
      return 3;
    },
  },
  methods: {
    async startCamera() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        
        const video = this.$refs.video;
        video.srcObject = this.stream;
        video.onloadedmetadata = () => {
          video.play();
          this.isCameraStarted = true;
        };
      } catch (err) {
        console.error('Error accessing camera:', err);
        this.error = 'Unable to access camera. Please ensure you have granted camera permissions.';
        alert(this.error);
      }
    },
    
    async optimizeImage(imageData, size = 280, quality = 0.8) {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = imageData;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw the image as is (already flipped during capture)
          ctx.drawImage(img, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
      });
    },

    async captureImage() {
      if (this.capturedImages.length >= 3) {
        alert('Maximum of 3 images allowed');
        return;
      }

      try {
        this.isProcessing = true;
        const video = this.$refs.video;
        const canvas = this.$refs.canvas;
        
        const guideSize = 280;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const centerX = (videoWidth - guideSize) / 2;
        const centerY = (videoHeight - guideSize) / 2;
        
        canvas.width = guideSize;
        canvas.height = guideSize;
        
        const context = canvas.getContext('2d');
        
        // Save the current context state
        context.save();
        
        // Flip the context horizontally to match the video display
        context.scale(-1, 1);
        context.translate(-guideSize, 0);
        
        // Draw the image flipped (same as display)
        context.drawImage(
          video,
          centerX, centerY,
          guideSize, guideSize,
          0, 0,
          guideSize, guideSize
        );
        
        // Restore the context
        context.restore();
        
        const rawImageData = canvas.toDataURL('image/jpeg', 1.0);
        const optimizedImage = await this.optimizeImage(rawImageData, 280, 0.8);
        
        this.capturedImages.push(optimizedImage);
        this.showCaptureEffect();
        
        const imageSize = Math.round((optimizedImage.length * 3) / 4);
        console.log(`Captured image size: ${(imageSize / 1024).toFixed(2)}KB`);
        
      } catch (error) {
        console.error('Error capturing image:', error);
        alert('Failed to capture image. Please try again.');
      } finally {
        this.isProcessing = false;
      }
    },

    validateImageSize(imageData) {
      const sizeInBytes = Math.round((imageData.length * 3) / 4);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      
      // Warn if image is too large (e.g., > 1MB)
      if (sizeInMB > 1) {
        console.warn(`Large image detected: ${sizeInMB.toFixed(2)}MB`);
        return false;
      }
      return true;
    },

    removeImage(index) {
      this.capturedImages.splice(index, 1);
    },

    showCaptureEffect() {
      // Add a flash effect or sound here if desired
      const flash = document.createElement('div');
      flash.className = 'camera-flash';
      this.$refs.video.parentElement.appendChild(flash);
      setTimeout(() => flash.remove(), 150);
    },

    showDialog(message, isSuccess = true) {
      this.dialog = {
        show: true,
        message,
        isSuccess
      };
    },

    closeDialog() {
      this.dialog.show = false;
      if (this.dialog.isSuccess) {
        this.resetForm();
      }
    },

    async submitRegistration() {
      if (this.isRegistering) return;

      if (!this.studentId) {
        this.showDialog('Select a student from college portal search first.', false);
        return;
      }
      
      try {
        this.isRegistering = true;
        
        const optimizedImages = await Promise.all(
          this.capturedImages.map(img => 
            this.validateImageSize(img) ? img : this.optimizeImage(img, 280, 0.7)
          )
        );

        // student_id = biometrics identity (EdStack Mongo ObjectId for edstack-fe;
        // EdClock integer for legacy college_portal).
        // edclock_student_id = EdClock integer when dual-id (edstack-fe) flow.
        const body = {
          student_id: String(this.studentId),
          images: optimizedImages
        };
        if (
          this.edClockStudentId &&
          String(this.edClockStudentId) !== String(this.studentId)
        ) {
          body.edclock_student_id = String(this.edClockStudentId);
        }

        const response = await fetch(edClockApiUrl('/api/v1/edclock/biometric-enroll'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.showDialog('Registration successful!', true);
        } else {
          const errorMessage = data?.data?.error || data?.error || data?.message || 'Registration failed';
          throw new Error(errorMessage);
        }
      } catch (err) {
        console.error('Error during registration:', err);
        this.showDialog(err.message || 'Registration failed. Please try again.', false);
      } finally {
        this.isRegistering = false;
      }
    },

    resetForm() {
      this.studentId = '';
      this.edClockStudentId = '';
      this.fullName = '';
      this.email = '';
      this.capturedImages = [];
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.isCameraStarted = false;
      }
    },

    handleSearch() {
      clearTimeout(this.debounceTimer);
      
      if (this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.debounceTimer = setTimeout(async () => {
        try {
          const response = await fetch(
            edClockApiUrl(
              `/api/v1/edclock/get-all-students?search=${encodeURIComponent(this.searchQuery)}`
            )
          );
          
          if (!response.ok) {
            throw new Error('Search failed');
          }

          const data = await response.json();
          
          if (data && data.data) {
            this.searchResults = data.data.slice(0, 5); // Limit to 5 results
            console.log('Search Results:', this.searchResults); // For debugging
          }
        } catch (error) {
          console.error('Error searching students:', error);
          this.searchResults = [];
        }
      }, 300);
    },

    selectStudent(student) {
      // Portal/edClock StudentResource.id is the shared student_id (legacy search).
      const id = student.id != null ? String(student.id) : '';
      this.studentId = id;
      this.edClockStudentId = id;
      this.fullName = student.name || '';
      this.email = student.email || '';
      this.searchQuery = student.name || '';
      this.searchResults = [];
    },

    // Add click outside handler to close search results
    handleClickOutside(event) {
      if (!event.target.closest('.search-container')) {
        this.showResults = false;
      }
    },

    async fetchStudentDetails(studentId) {
      try {
        const response = await fetch(edClockApiUrl(`/api/v1/edclock/get-student-detail?student_id=${studentId}`));
        const data = await response.json();
        
        if (data && data.data) {
          const student = data.data;
          const id = student.id != null ? String(student.id) : '';
          this.studentId = id;
          this.edClockStudentId = id;
          this.fullName = student.name || '';
          this.email = student.email || '';
          this.searchQuery = student.name || '';
        } else {
          console.error('Student details not found');
          alert('Student details not found');
        }
      } catch (error) {
        console.error('Error fetching student details:', error);
        alert('Error fetching student details');
      }
    },

    /**
     * edstack-fe: student_id=Mongo, edclock_student_id=integer, name/email in query.
     * Legacy college_portal: student_id=EdClock integer only → get-student-detail.
     */
    prefillFromQuery() {
      const studentId = this.$route.query.student_id;
      const edClockStudentId = this.$route.query.edclock_student_id;
      const name = this.$route.query.fullname;
      const email = this.$route.query.email;

      if (!studentId) {
        return;
      }

      if (edClockStudentId) {
        this.studentId = String(studentId);
        this.edClockStudentId = String(edClockStudentId);
        this.fullName = name ? String(name) : '';
        this.email = email ? String(email) : '';
        this.searchQuery = this.fullName;
        this.prefilledFromQuery = true;
        return;
      }

      this.prefilledFromQuery = false;
      this.fetchStudentDetails(studentId);
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    this.prefillFromQuery();
    if (this.isEmbedded) {
      document.documentElement.classList.add('biometrics-embedded');
      document.body.classList.add('biometrics-embedded');
    }
  },
  beforeUnmount() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    document.removeEventListener('click', this.handleClickOutside);
    document.documentElement.classList.remove('biometrics-embedded');
    document.body.classList.remove('biometrics-embedded');
  }
}
</script>

<style scoped>
.registration {
  min-height: 100vh;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.registration.embedded {
  min-height: 100%;
  height: 100%;
  padding: 0;
  align-items: stretch;
  justify-content: stretch;
}

.registration-container {
  width: 90%;
  max-width: 1400px;
  height: 85vh;
  display: flex;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.registration.embedded .registration-container {
  width: 100%;
  max-width: none;
  height: 100%;
  border-radius: 0;
  box-shadow: none;
}

/* Left Panel Styles */
.left-panel {
  width: 38%;
  min-width: 360px;
  max-width: 460px;
  background: #ffffff;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  border-right: 1px solid #e2e8f0;
  position: relative;
}

.header .eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0d9488;
}

.header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header .subtitle {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #64748b;
}

.steps {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.step-num {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  background: #e2e8f0;
  color: #64748b;
  flex-shrink: 0;
}

.step.active .step-num,
.step.done .step-num {
  background: #0d9488;
  color: #fff;
}

.step-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}

.step.active .step-label {
  color: #0f172a;
}

.step.done .step-label {
  color: #0d9488;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  min-width: 0.75rem;
}

.step-line.done {
  background: #99f6e4;
}

.student-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid #ccfbf1;
  background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 70%);
}

.student-avatar {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #fff;
  background: linear-gradient(135deg, #0d9488, #0f766e);
  flex-shrink: 0;
}

.student-card-body {
  min-width: 0;
  flex: 1;
}

.student-card-eyebrow {
  margin: 0;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0d9488;
}

.student-card-name {
  margin: 0.15rem 0 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-card-meta {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-card-check {
  color: #0d9488;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.photo-progress {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.photo-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.photo-progress-header h3 {
  margin: 0;
  font-size: 0.9rem;
  color: #0f172a;
}

.photo-count {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0d9488;
}

.photo-guidance {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
}

.photo-slots {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;
}

.photo-slot {
  position: relative;
  aspect-ratio: 1;
  border-radius: 14px;
  border: 1.5px dashed #cbd5e1;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  color: #94a3b8;
  overflow: hidden;
}

.photo-slot i {
  font-size: 1.35rem;
}

.photo-slot span {
  font-size: 0.7rem;
  font-weight: 600;
}

.photo-slot.filled {
  border-style: solid;
  border-color: #99f6e4;
}

.photo-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-slot .remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.camera-overlay-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #fff;
  max-width: 280px;
  padding: 1.5rem;
}

.overlay-icon {
  font-size: 2.5rem;
  opacity: 0.9;
}

.overlay-title {
  margin: 0.75rem 0 0.35rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.overlay-text {
  margin: 0 0 1.1rem;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.8);
}

.camera-btn {
  background: rgba(66, 185, 131, 0.9);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.capture-hint {
  position: static;
  transform: none;
  margin: 0;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

/* Search Styles */
.search-container {
  position: relative;
  width: 100%;
  z-index: 1000; /* Ensure dropdown appears above other elements */
}

.search-hint {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  background: white;
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Form Fields Styles */
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  position: relative;
  width: 100%;
}

.form-group i {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-group input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.form-group input[readonly] {
  background: #f1f5f9;
  cursor: not-allowed;
}

/* Right Panel Styles */
.right-panel {
  flex: 1;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.camera-section {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
}

/* Camera Frame Styles */
.camera-frame {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 10;
}

.frame-header {
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: #42b983;
  font-size: 0.8rem;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(66, 185, 131, 0.5);
}

.frame-corners {
  position: absolute;
  inset: 40px;
}

.corner {
  position: absolute;
  width: 50px;
  height: 50px;
  border: 3px solid #42b983;
  box-shadow: 0 0 10px rgba(66, 185, 131, 0.3);
}

.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
}

.frame-guide {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  border: 3px dashed rgba(66, 185, 131, 0.8);
  border-radius: 12px;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
  background-image: 
    linear-gradient(rgba(66, 185, 131, 0.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(66, 185, 131, 0.2) 1px, transparent 1px);
  background-size: 70px 70px;
  background-position: center center;
}

.frame-guide::after {
  content: 'Center your face in the square';
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(66, 185, 131, 1);
  font-size: 1.1rem;
  white-space: nowrap;
  font-weight: 500;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  background: rgba(0, 0, 0, 0.4);
  padding: 8px 16px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

/* Camera Controls */
.camera-controls {
  position: absolute;
  bottom: 48px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  z-index: 20;
}

.capture-btn {
  width: 70px;
  height: 70px;
  background: rgba(66, 185, 131, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.capture-btn:hover:not(:disabled) {
  transform: scale(1.1);
}

.capture-btn i {
  font-size: 2rem;
  color: white;
}

.camera-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 30;
}

/* Captured Images Styles */
.captured-images h3 {
  color: #1e293b;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.image-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Submit Button */
.submit-btn {
  margin-top: auto;
  background: #0d9488;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 48px;
}

.submit-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.submit-btn:hover:not(:disabled) {
  background: #0f766e;
  transform: translateY(-1px);
}

/* Search Results Dropdown */
.search-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
}

.search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f5f9;
}

.search-result-item:hover {
  background-color: #f8fafc;
}

.search-result-item:last-child {
  border-bottom: none;
}

.student-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.student-name {
  color: #1e293b;
  font-weight: 500;
}

.student-email {
  color: #64748b;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .registration-container {
    width: 95%;
  }

  .left-panel {
    min-width: 320px;
  }
}

@media (max-width: 900px) {
  .registration {
    align-items: stretch;
    padding: 0.75rem;
  }

  .registration.embedded {
    padding: 0;
  }

  .registration-container {
    width: 100%;
    max-width: none;
    height: auto;
    min-height: calc(100vh - 1.5rem);
    flex-direction: column;
    overflow: visible;
    border-radius: 16px;
  }

  .registration.embedded .registration-container {
    min-height: 100%;
    border-radius: 0;
  }

  .left-panel {
    width: 100%;
    min-width: 0;
    max-width: none;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding: 1.25rem;
    gap: 1rem;
    overflow: visible;
    order: 2;
  }

  .right-panel {
    order: 1;
    width: 100%;
    flex: none;
    padding: 0.75rem;
    min-height: 0;
  }

  .camera-section,
  .camera-container {
    height: auto;
  }

  .video-wrapper {
    width: 100%;
    height: min(62vh, 520px);
    border-radius: 12px;
  }

  .frame-corners {
    inset: 24px;
  }

  .corner {
    width: 36px;
    height: 36px;
  }

  .frame-header {
    top: 12px;
    font-size: 0.7rem;
    letter-spacing: 1px;
    padding: 0 0.75rem;
  }

  .frame-guide {
    width: min(62vw, 220px);
    height: min(62vw, 220px);
    background-size: 55px 55px;
  }

  .frame-guide::after {
    bottom: -36px;
    font-size: 0.85rem;
    padding: 6px 12px;
    white-space: normal;
    text-align: center;
    width: max-content;
    max-width: 90vw;
  }

  .camera-controls {
    bottom: 20px;
  }

  .capture-btn {
    width: 60px;
    height: 60px;
  }

  .capture-btn i {
    font-size: 1.6rem;
  }

  .capture-hint {
    white-space: normal;
    text-align: center;
    padding: 0 1rem;
    font-size: 0.75rem;
  }

  .camera-overlay-card {
    max-width: 90%;
    padding: 1rem;
  }

  .overlay-title {
    font-size: 1rem;
  }

  .overlay-text {
    font-size: 0.8rem;
  }

  .header h2 {
    font-size: 1.2rem;
  }

  .header .subtitle {
    font-size: 0.8rem;
  }

  .steps {
    padding: 0.65rem 0.7rem;
  }

  .step-label {
    font-size: 0.68rem;
  }

  .submit-btn {
    position: sticky;
    bottom: 0.5rem;
    z-index: 5;
  }
}

@media (max-width: 480px) {
  .registration {
    padding: 0.5rem;
  }

  .left-panel {
    padding: 1rem;
  }

  .right-panel {
    padding: 0.5rem;
  }

  .video-wrapper {
    height: min(58vh, 440px);
  }

  .frame-guide {
    width: min(58vw, 180px);
    height: min(58vw, 180px);
  }

  .step-label {
    display: none;
  }

  .student-card {
    padding: 0.85rem;
  }

  .photo-slots {
    gap: 0.5rem;
  }
}

/* Add loading animation styles */
.mdi-spin {
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Update capture button styles for loading state */
.capture-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Add these new styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;
}

.dialog {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
}

.success-icon {
  font-size: 4rem;
  color: #42b983;
}

.error-icon {
  font-size: 4rem;
  color: #ef4444;
}

.dialog h3 {
  color: #1e293b;
  font-size: 1.2rem;
  margin: 0;
}

.dialog-btn {
  background: #42b983;
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dialog-btn:hover {
  background: #3aa876;
  transform: translateY(-1px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

<style>
html.biometrics-embedded,
html.biometrics-embedded body,
html.biometrics-embedded #app,
html.biometrics-embedded .app {
  height: 100%;
  min-height: 100%;
  margin: 0;
  overflow: hidden;
}

@media (max-width: 900px) {
  html.biometrics-embedded,
  html.biometrics-embedded body,
  html.biometrics-embedded #app,
  html.biometrics-embedded .app {
    height: auto;
    min-height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
